import { zodResolver } from '@hookform/resolvers/zod';
import { serialize, useOnUpdate, useStateMerger } from '@wyz/utils';
import { useCallback, useEffectEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { IUserCustomer } from '~/common/types';
import { useManageWcwSearchHistory } from '~/purchase/api';
import { SearchHistoryHelpers, WcwSearchHelpers } from '~/purchase/helpers';
import { IWcwSearchFormSchema, wcwSearchFormSchema } from '~/purchase/schemas';
import { IWcwListState } from '~/purchase/types';

type Args = {
  customer: IUserCustomer;
  defaultValues?: Partial<IWcwSearchFormSchema>;
};

export const useWcwSearchForm = ({ customer, defaultValues }: Args) => {
  const [searchParams] = useSearchParams();

  const methods = useForm<IWcwSearchFormSchema>({
    resolver: zodResolver(wcwSearchFormSchema),
    defaultValues: {
      ...WcwSearchHelpers.getWcwSearchDefaultValues(),
      ...WcwSearchHelpers.parseWcwSearchFromQs(searchParams),
      ...defaultValues,
    },
    shouldUnregister: false,
  });

  const [listState, setter] = useStateMerger<IWcwListState>(
    WcwSearchHelpers.WCW_LIST_STATE_DEFAULT,
  );
  const navigate = useNavigate();
  const manageWcwSearchHistory = useManageWcwSearchHistory({
    create: { enabled: true },
  });

  const handleOnSearch = useCallback(
    (query: Partial<IWcwListState>) => {
      if (query.searchQuery) {
        manageWcwSearchHistory.create.mutate(
          SearchHistoryHelpers.buildWcwSearchPayload(query.searchQuery),
        );
      }
      void navigate({
        pathname: '/purchase/wcw/search/result',
        search: serialize(
          WcwSearchHelpers.serializeWcwSearchToQs(query.searchQuery),
        ),
      });
      setter(query);
    },
    [manageWcwSearchHistory.create, navigate, setter],
  );

  const resetForm = useEffectEvent(() => {
    setter(WcwSearchHelpers.WCW_LIST_STATE_DEFAULT);
    methods.reset(WcwSearchHelpers.getWcwSearchDefaultValues());
  });

  useOnUpdate(() => {
    if (customer.id) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      resetForm();
    }
  }, [customer.id]);

  return {
    vehicleTypesGroups: customer.settingTemplate?.vehicleTypeGroups ?? [],
    methods,
    listState,
    setter,
    onSubmit: handleOnSearch,
  };
};
